import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { PURPLE, PURPLE_DARK } from "./leadConstants";
import {
  deliveryAddressFromQuotation,
  normalizeDeliveryAddress,
  validateDeliveryAddress,
} from "../utils/quotationAddressUtils";
import { formatQuotationLabel } from "../utils/quotationUtils";
import QuotationService from "../services/QuotationService";

export default function QuotationConvertAddressDialog({
  open,
  quotation,
  onClose,
  onConfirm,
  submitting = false,
}) {
  const [form, setForm] = useState(deliveryAddressFromQuotation(quotation));
  const [error, setError] = useState("");
  const [paymentMode, setPaymentMode] = useState("cod");
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [onlineAvailable, setOnlineAvailable] = useState(false);

  useEffect(() => {
    if (open && quotation) {
      setForm(deliveryAddressFromQuotation(quotation));
      setError("");
      setOptionsLoading(true);
      QuotationService.getConvertOptions()
        .then((opts) => {
          const canPayOnline = Boolean(opts?.payment_modes?.online ?? opts?.native_payment_available);
          setOnlineAvailable(canPayOnline);
          setPaymentMode(canPayOnline ? "online" : "cod");
        })
        .catch(() => {
          setOnlineAvailable(false);
          setPaymentMode("cod");
        })
        .finally(() => setOptionsLoading(false));
    }
  }, [open, quotation]);

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    if (error) setError("");
  };

  const handleSubmit = () => {
    const normalized = normalizeDeliveryAddress(form);
    const validationError = validateDeliveryAddress(normalized);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (paymentMode === "online" && !onlineAvailable) {
      setError("Online payment is not configured. Enable Razorpay or UPI for order details in Payment settings, or choose COD.");
      return;
    }
    onConfirm?.(normalized, paymentMode);
  };

  if (!quotation) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LocalShippingOutlinedIcon sx={{ color: PURPLE }} />
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Convert to order
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatQuotationLabel(quotation.quotation_number)}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter delivery details and choose how the customer will pay.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2.5, p: 1.5, borderRadius: 1.5, border: "1px solid #e8edf3", bgcolor: "#fafbfc" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <PaymentsOutlinedIcon sx={{ fontSize: 18, color: PURPLE }} />
            <Typography variant="subtitle2" fontWeight={700}>
              Payment method
            </Typography>
            {optionsLoading && <CircularProgress size={14} sx={{ ml: 0.5 }} />}
          </Box>
          <FormControl component="fieldset" disabled={optionsLoading || submitting}>
            <RadioGroup
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
            >
              <FormControlLabel
                value="cod"
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Cash on delivery (COD)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Order is created immediately. No WhatsApp payment message.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="online"
                control={<Radio size="small" />}
                disabled={!onlineAvailable}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Online payment (WhatsApp)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {onlineAvailable
                        ? "Sends Review & Pay on WhatsApp; order is confirmed after payment."
                        : "Requires active Razorpay or UPI config for order details in Settings → Payment."}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Delivery address
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Customer name"
              value={form.name}
              onChange={updateField("name")}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              value={form.phone}
              onChange={updateField("phone")}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={updateField("email")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address line 1"
              value={form.address1}
              onChange={updateField("address1")}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address line 2"
              value={form.address2}
              onChange={updateField("address2")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="City"
              value={form.city}
              onChange={updateField("city")}
              fullWidth
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="State"
              value={form.state}
              onChange={updateField("state")}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="PIN code"
              value={form.pincode}
              onChange={updateField("pincode")}
              fullWidth
              size="small"
              required
              inputProps={{ maxLength: 6 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Country"
              value={form.country}
              onChange={updateField("country")}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || optionsLoading}
          sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK } }}
        >
          {submitting ? "Converting…" : "Convert to order"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
